import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Globe, 
  Link, 
  Server, 
  Plus,
  Loader2,
  CheckCircle,
  XCircle,
  Info
} from "lucide-react";
import { toast } from "sonner";

export default function AddScraperModal({ open, onClose, onScraperAdded }) {
  const [scraperType, setScraperType] = useState("job_board");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Common fields
  const [name, setName] = useState("");
  
  // Job Board fields
  const [baseUrl, setBaseUrl] = useState("");
  const [searchUrlTemplate, setSearchUrlTemplate] = useState("");
  
  // Custom URL fields
  const [targetUrl, setTargetUrl] = useState("");
  
  // API Source fields
  const [apiUrl, setApiUrl] = useState("");
  const [apiKeyEnv, setApiKeyEnv] = useState("");
  const [apiHeaders, setApiHeaders] = useState("");
  const [apiParams, setApiParams] = useState("");
  const [responsePath, setResponsePath] = useState("");
  
  // CSS Selectors
  const [jobCardSelector, setJobCardSelector] = useState(".job-card");
  const [titleSelector, setTitleSelector] = useState(".job-title");
  const [companySelector, setCompanySelector] = useState(".company-name");
  const [locationSelector, setLocationSelector] = useState(".location");
  const [linkSelector, setLinkSelector] = useState("a");

  const resetForm = () => {
    setName("");
    setBaseUrl("");
    setSearchUrlTemplate("");
    setTargetUrl("");
    setApiUrl("");
    setApiKeyEnv("");
    setApiHeaders("");
    setApiParams("");
    setResponsePath("");
    setJobCardSelector(".job-card");
    setTitleSelector(".job-title");
    setCompanySelector(".company-name");
    setLocationSelector(".location");
    setLinkSelector("a");
    setTestResult(null);
  };

  const buildScraperConfig = () => {
    const config = {
      name,
      scraper_type: scraperType,
      enabled: true,
    };

    if (scraperType === "job_board") {
      config.base_url = baseUrl;
      config.search_url_template = searchUrlTemplate || `${baseUrl}/jobs?q={keyword}&l={location}`;
      config.selectors = {
        job_card: jobCardSelector,
        title: titleSelector,
        company: companySelector,
        location: locationSelector,
        link: linkSelector,
      };
    } else if (scraperType === "custom_url") {
      config.target_url = targetUrl;
      config.selectors = {
        job_card: jobCardSelector,
        title: titleSelector,
        company: companySelector,
        location: locationSelector,
        link: linkSelector,
      };
    } else if (scraperType === "api_source") {
      config.api_url = apiUrl;
      config.api_key_env = apiKeyEnv;
      config.response_path = responsePath;
      
      try {
        config.api_headers = apiHeaders ? JSON.parse(apiHeaders) : {};
      } catch {
        config.api_headers = {};
      }
      
      try {
        config.api_params = apiParams ? JSON.parse(apiParams) : {};
      } catch {
        config.api_params = {};
      }
    }

    return config;
  };

  const handleTest = async () => {
    if (!name) {
      toast.error("Please enter a name for the scraper");
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const config = buildScraperConfig();
      const response = await api.post('/scrapers/custom/test', config);
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success(`Test successful! Found ${response.data.leads_found} leads`);
      } else {
        toast.error(`Test failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error("Test failed");
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!name) {
      toast.error("Please enter a name for the scraper");
      return;
    }

    setSaving(true);

    try {
      const config = buildScraperConfig();
      await api.post('/scrapers/custom', config);
      toast.success("Scraper created successfully!");
      resetForm();
      onScraperAdded?.();
      onClose();
    } catch (error) {
      toast.error("Failed to create scraper");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#264653] to-[#1a3a42] border-2 border-[#e9c46a]/30">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#e9c46a] flex items-center gap-2">
            <Plus className="w-6 h-6" />
            Add New Scraper
          </DialogTitle>
          <DialogDescription className="text-[#a8c8c4]">
            Create a custom scraper to fetch job listings from any source
          </DialogDescription>
        </DialogHeader>

        <Tabs value={scraperType} onValueChange={setScraperType} className="mt-4">
          <TabsList className="grid grid-cols-3 bg-[#1a3a42] border-2 border-[#2a9d8f]/30">
            <TabsTrigger 
              value="job_board" 
              className="data-[state=active]:bg-[#2a9d8f] data-[state=active]:text-white text-[#a8c8c4]"
              data-testid="tab-job-board"
            >
              <Globe className="w-4 h-4 mr-2" />
              Job Board
            </TabsTrigger>
            <TabsTrigger 
              value="custom_url"
              className="data-[state=active]:bg-[#2a9d8f] data-[state=active]:text-white text-[#a8c8c4]"
              data-testid="tab-custom-url"
            >
              <Link className="w-4 h-4 mr-2" />
              Custom URL
            </TabsTrigger>
            <TabsTrigger 
              value="api_source"
              className="data-[state=active]:bg-[#2a9d8f] data-[state=active]:text-white text-[#a8c8c4]"
              data-testid="tab-api-source"
            >
              <Server className="w-4 h-4 mr-2" />
              API Source
            </TabsTrigger>
          </TabsList>

          {/* Common Name Field */}
          <div className="mt-4 space-y-2">
            <Label className="text-[#e9c46a] font-mono text-xs">SCRAPER NAME *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Custom Job Board"
              className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
              data-testid="scraper-name-input"
            />
          </div>

          {/* Job Board Config */}
          <TabsContent value="job_board" className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-[#2a9d8f]/10 border border-[#2a9d8f]/30">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-[#40c9ba] mt-0.5" />
                <p className="text-xs text-[#a8c8c4]">
                  Job Board scrapers search for jobs using keywords and location. 
                  Use <code className="text-[#e9c46a]">{"{keyword}"}</code> and <code className="text-[#e9c46a]">{"{location}"}</code> placeholders in the URL template.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#a8c8c4] font-mono text-xs">BASE URL</Label>
              <Input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://example-jobs.com"
                className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
                data-testid="base-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#a8c8c4] font-mono text-xs">SEARCH URL TEMPLATE</Label>
              <Input
                value={searchUrlTemplate}
                onChange={(e) => setSearchUrlTemplate(e.target.value)}
                placeholder="{base_url}/search?q={keyword}&location={location}"
                className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
                data-testid="search-url-input"
              />
            </div>

            <SelectorFields
              jobCardSelector={jobCardSelector}
              setJobCardSelector={setJobCardSelector}
              titleSelector={titleSelector}
              setTitleSelector={setTitleSelector}
              companySelector={companySelector}
              setCompanySelector={setCompanySelector}
              locationSelector={locationSelector}
              setLocationSelector={setLocationSelector}
              linkSelector={linkSelector}
              setLinkSelector={setLinkSelector}
            />
          </TabsContent>

          {/* Custom URL Config */}
          <TabsContent value="custom_url" className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-[#f4a261]/10 border border-[#f4a261]/30">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-[#f4a261] mt-0.5" />
                <p className="text-xs text-[#a8c8c4]">
                  Custom URL scrapers fetch jobs from a single page URL. 
                  Great for scraping specific job listing pages or career sites.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#a8c8c4] font-mono text-xs">TARGET URL *</Label>
              <Input
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
                placeholder="https://company.com/careers/jobs"
                className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
                data-testid="target-url-input"
              />
            </div>

            <SelectorFields
              jobCardSelector={jobCardSelector}
              setJobCardSelector={setJobCardSelector}
              titleSelector={titleSelector}
              setTitleSelector={setTitleSelector}
              companySelector={companySelector}
              setCompanySelector={setCompanySelector}
              locationSelector={locationSelector}
              setLocationSelector={setLocationSelector}
              linkSelector={linkSelector}
              setLinkSelector={setLinkSelector}
            />
          </TabsContent>

          {/* API Source Config */}
          <TabsContent value="api_source" className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-[#e76f51]/10 border border-[#e76f51]/30">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-[#e76f51] mt-0.5" />
                <p className="text-xs text-[#a8c8c4]">
                  API Source scrapers fetch jobs from JSON APIs. 
                  Configure the endpoint, headers, and response path to extract job data.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#a8c8c4] font-mono text-xs">API URL *</Label>
              <Input
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://api.example.com/v1/jobs"
                className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
                data-testid="api-url-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#a8c8c4] font-mono text-xs">API KEY ENV VAR</Label>
                <Input
                  value={apiKeyEnv}
                  onChange={(e) => setApiKeyEnv(e.target.value)}
                  placeholder="MY_API_KEY"
                  className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#a8c8c4] font-mono text-xs">RESPONSE PATH</Label>
                <Input
                  value={responsePath}
                  onChange={(e) => setResponsePath(e.target.value)}
                  placeholder="data.jobs"
                  className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#a8c8c4] font-mono text-xs">HEADERS (JSON)</Label>
              <Textarea
                value={apiHeaders}
                onChange={(e) => setApiHeaders(e.target.value)}
                placeholder='{"X-Api-Key": "your-key", "Accept": "application/json"}'
                className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0] font-mono text-xs h-20"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#a8c8c4] font-mono text-xs">QUERY PARAMS (JSON)</Label>
              <Textarea
                value={apiParams}
                onChange={(e) => setApiParams(e.target.value)}
                placeholder='{"page": "1", "limit": "20"}'
                className="bg-[#1a3a42] border-2 border-[#2a9d8f]/40 text-[#faf3e0] font-mono text-xs h-20"
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Test Result */}
        {testResult && (
          <div className={`mt-4 p-4 rounded-lg border-2 ${
            testResult.success 
              ? 'bg-[#2a9d8f]/10 border-[#2a9d8f]/40' 
              : 'bg-[#e76f51]/10 border-[#e76f51]/40'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-[#2a9d8f]" />
              ) : (
                <XCircle className="w-5 h-5 text-[#e76f51]" />
              )}
              <span className={`font-semibold ${testResult.success ? 'text-[#2a9d8f]' : 'text-[#e76f51]'}`}>
                {testResult.success ? 'Test Successful' : 'Test Failed'}
              </span>
            </div>
            {testResult.success ? (
              <div className="text-sm text-[#a8c8c4]">
                <p>Found {testResult.leads_found} job listings</p>
                {testResult.sample_leads?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs font-mono text-[#e9c46a]">SAMPLE RESULTS:</p>
                    {testResult.sample_leads.map((lead, i) => (
                      <p key={i} className="text-xs truncate">
                        {lead.title} - {lead.client}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#e76f51]">{testResult.error}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testing || !name}
            className="flex-1 border-2 border-[#f4a261]/40 text-[#f4a261] hover:bg-[#f4a261]/20"
            data-testid="test-scraper-btn"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Test Scraper
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name}
            className="flex-1 bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-white border-2 border-[#40c9ba]/50"
            data-testid="save-scraper-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Save Scraper
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SelectorFields({
  jobCardSelector, setJobCardSelector,
  titleSelector, setTitleSelector,
  companySelector, setCompanySelector,
  locationSelector, setLocationSelector,
  linkSelector, setLinkSelector,
}) {
  return (
    <div className="space-y-3 pt-2 border-t border-[#2a9d8f]/20">
      <Label className="text-[#e9c46a] font-mono text-xs">CSS SELECTORS (Optional - AI will auto-detect if empty)</Label>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#a8c8c4] font-mono text-[10px]">JOB CARD</Label>
          <Input
            value={jobCardSelector}
            onChange={(e) => setJobCardSelector(e.target.value)}
            placeholder=".job-card"
            className="bg-[#1a3a42] border border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[#a8c8c4] font-mono text-[10px]">TITLE</Label>
          <Input
            value={titleSelector}
            onChange={(e) => setTitleSelector(e.target.value)}
            placeholder=".job-title"
            className="bg-[#1a3a42] border border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[#a8c8c4] font-mono text-[10px]">COMPANY</Label>
          <Input
            value={companySelector}
            onChange={(e) => setCompanySelector(e.target.value)}
            placeholder=".company-name"
            className="bg-[#1a3a42] border border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[#a8c8c4] font-mono text-[10px]">LOCATION</Label>
          <Input
            value={locationSelector}
            onChange={(e) => setLocationSelector(e.target.value)}
            placeholder=".location"
            className="bg-[#1a3a42] border border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[#a8c8c4] font-mono text-[10px]">LINK SELECTOR</Label>
        <Input
          value={linkSelector}
          onChange={(e) => setLinkSelector(e.target.value)}
          placeholder="a"
          className="bg-[#1a3a42] border border-[#2a9d8f]/30 text-[#faf3e0] text-sm h-8"
        />
      </div>
    </div>
  );
}
